import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, ShieldCheck, Wifi, Mail, ShieldAlert, Monitor,
  ArrowRight, Star, MapPin, Laptop, Printer, HeartHandshake,
  Network, Satellite, HardDrive, Tag, BadgeCheck, GraduationCap,
} from "lucide-react";
import SEO, { LOCAL_BUSINESS_SCHEMA } from "../components/SEO";
import GoogleReviewsCarousel from "../components/GoogleReviewsCarousel";
import FleetSection from "../components/FleetSection";
import { PHONE, PHONE_TEL, regions } from "../data/content";
import { trackEvent } from "../lib/analytics";

const homeFaqs = [
  {
    q: "How much does a home visit cost?",
    a: "From $139 for the first hour, $99 for the second and $79 for the third — EFTPOS or cash payment upon job completion, no callout fee. Full details are on our Pricing page.",
  },
  {
    q: "Do you really come to my home?",
    a: "Yes — Tech Emergency is a mobile service. We come to your home anywhere across metropolitan Adelaide, so you never have to unplug anything or carry a computer to a shop.",
  },
  {
    q: "What kinds of problems do you fix?",
    a: "Slow or broken computers and laptops, Wi-Fi and internet dropouts, printers that won't print, email problems, viruses and malware, new device setup, data recovery and general home technology help.",
  },
  {
    q: "Can you help seniors who aren't confident with technology?",
    a: "Absolutely — patient, plain-English help for seniors is one of the things we do most. We explain everything clearly, never rush, and write down the steps so you can do it yourself next time.",
  },
  {
    q: "What if you can't fix the problem?",
    a: "Our no fix · no fee guarantee applies — if we can't resolve the issue we came for, you don't pay for the repair work. All our labour is also backed by a 7-day warranty.",
  },
];

const quickServices = [
  { icon: Monitor, label: "Computer Repairs Adelaide", to: "/services/computer-repairs" },
  { icon: Laptop, label: "Laptop Troubleshooting & Repairs", to: "/services/computer-repairs" },
  { icon: Wifi, label: "Home Wi-Fi & Internet Help", to: "/services/wifi-troubleshooting" },
  { icon: Printer, label: "Printer Setup & Troubleshooting", to: "/services/tech-support" },
  { icon: Mail, label: "Email & Software Assistance", to: "/services/email-solutions" },
  { icon: ShieldAlert, label: "Virus & Malware Help", to: "/services/anti-scamming-cyber-awareness" },
  { icon: HeartHandshake, label: "Patient Computer Help for Seniors", to: "/services/tech-support" },
  { icon: GraduationCap, label: "Student Laptop & Software Support", to: "/services/computer-repairs" },
  { icon: Network, label: "Home UniFi Networking (Ubiquiti)", to: "/services/unifi-home-networking-adelaide" },
  { icon: Satellite, label: "Starlink Setup & Troubleshooting", to: "/services/starlink-setup-troubleshooting-adelaide" },
  { icon: HardDrive, label: "Data Recovery & Backups", to: "/services/data-recovery" },
];

const trustItems = [
  {
    icon: Star,
    value: "5.0 on Google",
    label: "Genuine customer reviews",
    href: "https://www.google.com/search?q=tech+emergency+adelaide+reviews",
    testid: "trust-google-rating",
  },
  { icon: ShieldCheck, value: "Police-checked", label: "Your local technician", testid: "trust-police-checked" },
  { icon: Tag, value: "Transparent pricing", label: "From $139 · no callout fee", testid: "trust-pricing" },
  { icon: BadgeCheck, value: "7-day warranty", label: "On all our labour", testid: "trust-warranty" },
];

const jsonLd = [
  LOCAL_BUSINESS_SCHEMA,
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: homeFaqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
];

export default function Home() {
  return (
    <>
      <SEO
        title="Computer Repairs Adelaide | Home IT Support | Tech Emergency"
        description="Computer repairs and home IT support across Adelaide. Friendly help with PCs, laptops, Wi-Fi, printers and email. Home visits from $139. Call Tech Emergency."
        path="/"
        image="https://images.unsplash.com/photo-1597852074816-d933c7d2b988?crop=entropy&cs=srgb&fm=jpg&q=85"
        jsonLd={jsonLd}
      />

      {/* HERO — photographic */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <img
          src="/images/hero-home.jpg"
          alt="Home IT support — a laptop being repaired in an Adelaide home"
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to right, rgba(5,5,5,0.9) 0%, rgba(5,5,5,0.75) 45%, rgba(5,5,5,0.4) 100%)",
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-28 pointer-events-none"
          style={{ background: "linear-gradient(to top, var(--te-bg), transparent)" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4 sm:pt-20 sm:pb-24 md:pt-28 md:pb-32">
          <div className="max-w-2xl te-reveal">
            <div className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 border border-white/20 bg-black/40 backdrop-blur-sm mb-7">
              <span className="w-2 h-2 rounded-full te-pulse" style={{ background: "var(--te-primary)" }} />
              <span className="te-overline text-[10px]">Mobile service · Metropolitan Adelaide</span>
            </div>

            <h1 className="mb-4 sm:mb-6" data-testid="hero-h1">
              <span className="block text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08]">
                Computer Repairs Adelaide
              </span>{" "}
              <span className="mt-2 sm:mt-4 flex items-center gap-3 text-xl sm:text-2xl font-medium text-zinc-100">
                <span className="hidden sm:block w-10 h-[3px]" style={{ background: "var(--te-primary)" }} aria-hidden="true" />
                We Come to You
              </span>
            </h1>

            <p className="text-base sm:text-lg text-zinc-200 leading-relaxed mb-3 sm:mb-4 max-w-xl" data-testid="hero-intro">
              Friendly, professional computer repairs and home IT support across Adelaide.
              We come to you to fix computers, laptops, Wi-Fi, printers and email problems.
            </p>
            <p className="text-sm sm:text-base text-zinc-300 leading-relaxed mb-4 sm:mb-8 max-w-xl" data-testid="hero-audience-line">
              Patient, plain-English help for households, seniors and students alike.
            </p>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-7">
              <a
                href={PHONE_TEL}
                data-testid="hero-call-btn"
                onClick={() => trackEvent("phone_call_click", { location: "home_hero" })}
                className="te-btn te-btn-primary text-base px-8 py-3 sm:py-4 justify-center whitespace-nowrap"
              >
                <Phone size={18} /> Call {PHONE}
              </a>
              <Link
                to="/book"
                data-testid="hero-enquiry-btn"
                className="te-btn te-btn-ghost text-base px-8 py-3 sm:py-4 justify-center bg-black/40 backdrop-blur-sm"
              >
                Send an Enquiry <ArrowRight size={16} />
              </Link>
            </div>

            <p className="hidden sm:block text-sm sm:text-base text-zinc-200 mb-3 sm:mb-4" data-testid="hero-pricing-line">
              <strong className="text-white">From $139 for the first hour</strong> — no callout fee.{" "}
              <Link to="/pricing" className="underline underline-offset-4 text-white hover:text-zinc-300">
                See full pricing
              </Link>
            </p>

            <div
              className="hidden sm:inline-flex items-center gap-2.5 text-sm text-zinc-300"
              data-testid="hero-eftpos-note"
            >
              <img src="/images/tap-and-go.png" alt="Tap and go contactless payment" className="h-5 sm:h-6 w-auto opacity-90" />
              <span className="sm:hidden">Easy Payment Onsite — EFTPOS or cash.</span>
              <span className="hidden sm:inline">Easy Payment Onsite — EFTPOS or cash upon job completion.</span>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST ROW — static, genuine */}
      <section className="border-b border-white/10" style={{ backgroundColor: "var(--te-paper)" }} data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7 md:py-8 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6">
          {trustItems.map((t) => {
            const inner = (
              <div className="flex items-start gap-3">
                <t.icon
                  size={20}
                  className="flex-shrink-0 mt-0.5"
                  style={{ color: "var(--te-primary)" }}
                  {...(t.icon === Star ? { fill: "#FFCC00", stroke: "none" } : {})}
                />
                <div>
                  <div className="font-semibold text-sm sm:text-base leading-tight">{t.value}</div>
                  <div className="text-xs sm:text-sm text-zinc-500 mt-1">{t.label}</div>
                </div>
              </div>
            );
            return t.href ? (
              <a
                key={t.value}
                href={t.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={t.testid}
                className="hover:opacity-80 transition-opacity"
              >
                {inner}
              </a>
            ) : (
              <div key={t.value} data-testid={t.testid}>{inner}</div>
            );
          })}
          <div className="sm:hidden col-span-2 mt-1 pt-5 border-t border-white/10 text-center" data-testid="mobile-pricing-eftpos">
            <p className="text-sm text-zinc-200 mb-2.5" data-testid="hero-pricing-line-mobile">
              <strong className="text-white">From $139 for the first hour</strong> — no callout fee.{" "}
              <Link to="/pricing" className="underline underline-offset-4 text-white">See full pricing</Link>
            </p>
            <div className="inline-flex items-center justify-center gap-2.5 text-sm text-zinc-300" data-testid="hero-eftpos-note-mobile">
              <img src="/images/tap-and-go.png" alt="Tap and go contactless payment" className="h-5 w-auto opacity-90" />
              Easy Payment Onsite — EFTPOS or cash.
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE HELP WITH — quick keyword links */}
      <section className="py-12 md:py-20" data-testid="quick-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-4">/ What we help with</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-10">
            Friendly help with the tech you use every day.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            {quickServices.map((s) => (
              <Link
                key={s.label}
                to={s.to}
                data-testid={`quick-service-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="te-card group p-6 flex items-center gap-4"
                style={{ backgroundColor: "var(--te-bg)" }}
              >
                <s.icon size={22} className="flex-shrink-0" style={{ color: "var(--te-primary)" }} />
                <span className="font-semibold text-sm sm:text-base group-hover:text-[var(--te-primary)] transition-colors">{s.label}</span>
              </Link>
            ))}
            <a
              href={PHONE_TEL}
              onClick={() => trackEvent("phone_call_click", { location: "quick_services" })}
              data-testid="quick-service-call"
              className="te-card group p-6 flex items-center gap-4"
              style={{ backgroundColor: "var(--te-bg)" }}
            >
              <Phone size={22} className="flex-shrink-0" style={{ color: "var(--te-primary)" }} />
              <span className="font-semibold text-sm sm:text-base">Something else? Call {PHONE}</span>
            </a>
          </div>
        </div>
      </section>

      {/* ZERO CALLOUT FEE + ELECTRIC FLEET */}
      <FleetSection />

      {/* MEET TOM */}
      <section className="py-12 md:py-20 border-y border-white/10" style={{ backgroundColor: "var(--te-paper)" }} data-testid="meet-tom-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4 max-w-xs lg:max-w-none">
            <img
              src="/images/tom.jpg"
              alt="Tom — your local Adelaide computer technician"
              className="w-full border border-white/10"
              loading="lazy"
              data-testid="tom-photo"
            />
          </div>
          <div className="lg:col-span-8">
            <div className="te-overline mb-4">/ Meet your local computer technician</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-5">
              Hi, I'm Tom.
            </h2>
            <p className="text-zinc-300 leading-relaxed mb-4 max-w-2xl">
              I provide friendly, straightforward computer repairs and home technology assistance
              throughout Adelaide.
            </p>
            <p className="text-zinc-400 leading-relaxed mb-7 max-w-2xl">
              I understand how frustrating technology problems can be. My aim is to explain things
              clearly, help resolve the problem and make the experience as straightforward as
              possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={PHONE_TEL}
                onClick={() => trackEvent("phone_call_click", { location: "meet_tom" })}
                data-testid="tom-call-btn"
                className="te-btn te-btn-primary"
              >
                <Phone size={16} /> Call {PHONE}
              </a>
              <Link to="/about" data-testid="tom-about-btn" className="te-btn te-btn-ghost">
                More about Tech Emergency <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRANSPARENT PRICING */}
      <section className="py-12 md:py-20" data-testid="pricing-teaser-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-4">/ Transparent pricing</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-10">
            Know what you'll pay before we arrive.
          </h2>
          <div className="grid sm:grid-cols-3 gap-px mb-8" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            {[
              { h: "First hour", p: "$139" },
              { h: "Second hour", p: "$99" },
              { h: "Third hour", p: "$79" },
            ].map((r, i) => (
              <div key={r.h} className="p-7" style={{ backgroundColor: "var(--te-bg)" }}>
                <div className="te-overline mb-2">{r.h}</div>
                <div className="text-4xl font-bold tracking-tight" style={{ color: i === 0 ? "var(--te-primary)" : "#fff" }}>{r.p}</div>
              </div>
            ))}
          </div>
          <p className="text-zinc-400 mb-8 max-w-2xl">
            No callout fee. If more time is needed, we discuss it with you before continuing.
            Easy Payment Onsite — EFTPOS or cash payment upon job completion.
          </p>
          <Link to="/pricing" data-testid="pricing-teaser-btn" className="te-btn te-btn-ghost">
            See full pricing details <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <GoogleReviewsCarousel />

      {/* COVERAGE */}
      <section className="py-12 md:py-20" data-testid="coverage-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
            <div className="lg:col-span-8">
              <div className="te-overline mb-4">/ Areas we service</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                Mobile. On-site. Across metropolitan Adelaide.
              </h2>
            </div>
            <p className="lg:col-span-4 text-zinc-400 leading-relaxed">
              No shop, no waiting room. We come to your home across Adelaide's northern, southern,
              eastern and western suburbs — subject to availability.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            {regions.map((r) => (
              <div
                key={r.name}
                className="p-8"
                style={{ backgroundColor: "var(--te-bg)" }}
                data-testid={`region-${r.name.toLowerCase().replace(/\s|&/g, "")}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <MapPin size={16} style={{ color: "var(--te-primary)" }} />
                  <div className="font-bold tracking-tight">{r.name}</div>
                </div>
                <ul className="space-y-1.5 text-sm text-zinc-400">
                  {r.suburbs.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link to="/suburbs" data-testid="all-suburbs-btn" className="te-btn te-btn-ghost">
              See every suburb we cover <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-20 border-t border-white/10" data-testid="home-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-4">/ Frequently asked questions</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-10">
            Good questions, honest answers.
          </h2>
          <div className="space-y-6">
            {homeFaqs.map((f) => (
              <div key={f.q} className="border-b border-white/10 pb-6">
                <h3 className="font-semibold text-lg mb-2">{f.q}</h3>
                <p className="text-sm sm:text-base text-zinc-400 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT & BOOKING CTA */}
      <section
        className="py-12 md:py-20 relative overflow-hidden border-y border-white/10"
        style={{ backgroundColor: "var(--te-paper)" }}
        data-testid="bottom-cta"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-6">
            Something broken? <span style={{ color: "var(--te-primary)" }}>Call us now.</span>
          </h2>
          <p className="text-zinc-400 mb-8 max-w-lg mx-auto leading-relaxed">
            Mon–Sat, 9am–7pm. Talk to a real Adelaide-based technician — not a call centre.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={PHONE_TEL}
              onClick={() => trackEvent("phone_call_click", { location: "bottom_cta" })}
              data-testid="cta-call-btn"
              className="te-btn te-btn-primary text-base px-8 py-4"
            >
              <Phone size={18} /> {PHONE}
            </a>
            <Link to="/book" data-testid="cta-book-btn" className="te-btn te-btn-ghost text-base px-8 py-4">
              Send an enquiry <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
